import React, { useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import _ from 'lodash';
import commaNumber from 'comma-number';
import './NotificationsCatalog.scss';

import { UNCLASSIFIED, humanizeName, humanizeCategory } from '../../helpers/notification_helpers';
import { ROLLOUT_MODES } from '../../helpers/notification_registry_helpers';
import HeaderToolbar from './elements/HeaderToolbar';

const CATEGORY_MAP = {
  opportunities: ['opportunity', 'brand_sends_opportunity', 'budget_limit'],
  gifting: ['lookbook', 'gifting', 'sample'],
  payouts: ['payout', 'first_payout', 'stripe', 'w9', 'tax'],
  collaborations: ['collaboration', 'contract', 'task', 'collab'],
  shop: ['shop', 'collection', 'pin', 'shelf', 'product'],
  codes: ['code', 'custom_code', 'discount'],
  performance: ['performance', 'weekly_creator', 'monthly'],
  account: ['welcome', 'upgraded', 'tier', 'waitlist', 'acceptance', 'onboard', 'verification'],
  brand: ['brand_', 'subscription', 'invoice'],
  social: ['social', 'mention', 'tiktok', 'instagram', 'youtube'],
  chat: ['chat', 'message'],
  announcements: ['announcement', 'newsletter'],
  concierge: ['concierge', 'noir', 'shopper']
};

const inferCategory = name => {
  for (const [category, keywords] of Object.entries(CATEGORY_MAP)) {
    if (keywords.some(kw => name.includes(kw))) return category;
  }
  return 'other';
};

// The registry's Product Area options, in the order the facet shows them. A template with a
// registry row takes its areas from there; one without falls back to inferCategory's keyword guess.
const REGISTRY_PRODUCT_AREAS = [
  'Opportunities',
  'Gifting',
  'Spotlights',
  'Chat & Brand Comms',
  'Links & Affiliate',
  'Earnings & Payments',
  'Onboarding',
  'ShopMy/Administrative'
];
const productAreasFor = template => {
  const fromRegistry = template.registry?.productAreas || [];
  return fromRegistry.length ? fromRegistry : [humanizeName(inferCategory(template.name))];
};

const ALL_CATEGORY = 'all';

// The budget page's seven categories, in its order, then the templates that have none.
const BUDGET_CATEGORIES = ['performance_reward', 'response_needed', 'operational', 'onboarding', 'editorial', 're_engagement', 'action_prompt'];
const CHANNELS = ['email', 'push', 'klaviyo', 'sms'];
// The facet reads the launch ladder top down, live first.
const LAUNCH_MODES = [...ROLLOUT_MODES].reverse();

// One line for the card's corner: what the ledger says this template did in the last 7 days.
const receiptsSummary = receipts => {
  if (!receipts || !receipts.total) return '7d · 0';
  const parts = [];
  if (receipts.sent) parts.push(`${commaNumber(receipts.sent)} sent`);
  if (receipts.shadow) parts.push(`${commaNumber(receipts.shadow)} shadow`);
  const held = _.sum(Object.values(receipts.held || {}));
  if (held) parts.push(`${commaNumber(held)} held`);
  if (receipts.failed) parts.push(`${commaNumber(receipts.failed)} failed`);
  return `7d · ${parts.join(' · ')}`;
};

const NotificationsCatalog = props => {
  const { templates, isFetching, onSelectTemplate, toolbarSlot = null } = props;

  // Search
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef(null);

  // Facets: product area (registry row, else inferred from the name), budget category (from the template), channel, launch mode
  const [activeCategory, setActiveCategory] = useState(ALL_CATEGORY);
  const [activeBudget, setActiveBudget] = useState(ALL_CATEGORY);
  const [activeChannel, setActiveChannel] = useState(ALL_CATEGORY);
  const [activeMode, setActiveMode] = useState(ALL_CATEGORY);

  // Categorized templates
  const categorizedTemplates = useMemo(() => {
    return templates.map(t => ({ ...t, areas: productAreasFor(t), budget: t.budgetCategory || UNCLASSIFIED }));
  }, [templates]);

  // Available product areas (only those with templates): the registry's areas first, then any leftover guesses, then Other
  const categoryTabs = useMemo(() => {
    const counts = _.countBy(_.flatMap(categorizedTemplates, 'areas'));
    const guessed = _.difference(Object.keys(counts), REGISTRY_PRODUCT_AREAS, ['Other']).sort();
    const tabs = [{ key: ALL_CATEGORY, label: 'All', count: categorizedTemplates.length }];
    [...REGISTRY_PRODUCT_AREAS, ...guessed, 'Other'].forEach(area => {
      if (counts[area]) tabs.push({ key: area, label: area, count: counts[area] });
    });
    return tabs;
  }, [categorizedTemplates]);

  const budgetTabs = useMemo(() => {
    const counts = _.countBy(categorizedTemplates, 'budget');
    const tabs = [{ key: ALL_CATEGORY, label: 'All', count: categorizedTemplates.length }];
    [...BUDGET_CATEGORIES, UNCLASSIFIED].forEach(cat => {
      if (counts[cat]) tabs.push({ key: cat, label: humanizeCategory(cat), count: counts[cat], attention: cat === UNCLASSIFIED });
    });
    return tabs;
  }, [categorizedTemplates]);

  const channelTabs = useMemo(() => {
    const tabs = [{ key: ALL_CATEGORY, label: 'All', count: categorizedTemplates.length }];
    CHANNELS.forEach(ch => {
      const count = categorizedTemplates.filter(t => t.channels?.includes(ch)).length;
      if (count) tabs.push({ key: ch, label: ch === 'sms' ? 'SMS' : humanizeName(ch), count });
    });
    return tabs;
  }, [categorizedTemplates]);

  const modeTabs = useMemo(() => {
    const counts = _.countBy(categorizedTemplates, t => t.mode?.mode);
    const tabs = [{ key: ALL_CATEGORY, label: 'All', count: categorizedTemplates.length }];
    LAUNCH_MODES.forEach(mode => {
      if (counts[mode]) tabs.push({ key: mode, label: humanizeName(mode), count: counts[mode] });
    });
    return tabs;
  }, [categorizedTemplates]);

  // Filtered results
  const filteredTemplates = useMemo(() => {
    let results = categorizedTemplates;
    if (activeCategory !== ALL_CATEGORY) {
      results = results.filter(t => t.areas.includes(activeCategory));
    }
    if (activeBudget !== ALL_CATEGORY) {
      results = results.filter(t => t.budget === activeBudget);
    }
    if (activeChannel !== ALL_CATEGORY) {
      results = results.filter(t => t.channels?.includes(activeChannel));
    }
    if (activeMode !== ALL_CATEGORY) {
      results = results.filter(t => t.mode?.mode === activeMode);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      results = results.filter(t => {
        const nameMatch = t.name.toLowerCase().includes(q);
        const subjectMatch = t.subject?.toLowerCase().includes(q);
        const channelMatch = t.channels?.some(ch => ch.toLowerCase().includes(q));
        return nameMatch || subjectMatch || channelMatch;
      });
    }
    return _.sortBy(results, 'name');
  }, [categorizedTemplates, activeCategory, activeBudget, activeChannel, activeMode, searchQuery]);

  if (isFetching) {
    return (
      <div className='notifications-catalog-outer'>
        <div className='catalog-loading'>
          {_.range(12).map(i => (
            <div key={i} className='skeleton-card' />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className='notifications-catalog-outer'>
      <div className='facet-rows'>
        <FacetRow label='Product area' tabs={categoryTabs} active={activeCategory} onSelect={setActiveCategory} />
        <FacetRow label='Budget category' tabs={budgetTabs} active={activeBudget} onSelect={setActiveBudget} />
        <FacetRow label='Channel' tabs={channelTabs} active={activeChannel} onSelect={setActiveChannel} />
        <FacetRow label='Launch mode' tabs={modeTabs} active={activeMode} onSelect={setActiveMode} />
      </div>

      <HeaderToolbar slot={toolbarSlot} className='notifications-catalog-outer'>
        <div className='catalog-toolbar'>
          <div className='results-summary'>
            {searchQuery
              ? `${filteredTemplates.length} result${filteredTemplates.length !== 1 ? 's' : ''} for "${searchQuery}"`
              : `Showing ${filteredTemplates.length} of ${categorizedTemplates.length} templates`}
          </div>
          <div className='search-container'>
            <input
              ref={searchInputRef}
              className='search-input'
              type='text'
              placeholder='Search templates...'
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <div
                className='search-clear'
                onClick={() => {
                  setSearchQuery('');
                  searchInputRef.current?.focus();
                }}
              >
                &times;
              </div>
            )}
          </div>
        </div>
      </HeaderToolbar>

      {/* Grid */}
      {filteredTemplates.length === 0 ? (
        <div className='catalog-empty'>No templates match your search.</div>
      ) : (
        <div className='catalog-grid'>
          {filteredTemplates.map(template => (
            <TemplateCard key={template.name} template={template} onSelect={onSelectTemplate} />
          ))}
        </div>
      )}
    </div>
  );
};

const FacetRow = ({ label, tabs, active, onSelect }) => (
  <div className='facet-row'>
    <span className='facet-label'>{label}</span>
    <div className='category-tabs'>
      {tabs.map(tab => (
        <div key={tab.key} className={cn('category-tab', { active: active === tab.key, attention: tab.attention })} onClick={() => onSelect(tab.key)}>
          {tab.label}
          <span className='count'>{tab.count}</span>
        </div>
      ))}
    </div>
  </div>
);

FacetRow.propTypes = {
  label: PropTypes.string.isRequired,
  tabs: PropTypes.array.isRequired,
  active: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired
};

const TemplateCard = ({ template, onSelect }) => {
  const { name, subject, channels, thumbnailHtml, push, event, areas, budget, userNotificationSetting, receipts7d } = template;
  const iframeRef = useRef(null);
  const isPushOnly = !thumbnailHtml && push && !push.error;
  const isEventOnly = !thumbnailHtml && !isPushOnly && !!event;
  const isUnclassified = budget === UNCLASSIFIED;

  const writeIframeContent = iframe => {
    if (!iframe || !thumbnailHtml) return;
    iframeRef.current = iframe;
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;
    doc.open();
    doc.write(`
      <html>
        <head><style>body { margin: 0; overflow: hidden; transform-origin: top left; transform: scale(0.35); width: 285.7%; }</style></head>
        <body>${thumbnailHtml}</body>
      </html>
    `);
    doc.close();
  };

  return (
    <div className='template-card' onClick={() => onSelect?.(template)}>
      <div className={cn('card-preview', { 'push-only': isPushOnly })}>
        {thumbnailHtml ? (
          <iframe ref={writeIframeContent} title={name} sandbox='allow-same-origin' scrolling='no' className='preview-iframe' />
        ) : isPushOnly ? (
          <CardPushPreview push={push} />
        ) : isEventOnly ? (
          <CardEventPreview event={event} />
        ) : (
          <div className='no-preview'>No preview</div>
        )}
        <span className='card-stat'>{receiptsSummary(receipts7d)}</span>
      </div>
      <div className='card-body'>
        <div className='card-name'>
          {humanizeName(name)}
          {isUnclassified && <span className='card-flag'>Unclassified</span>}
        </div>
        {subject ? (
          <div className='card-subject'>{subject}</div>
        ) : (
          isEventOnly && <div className='card-subject'>Fires the Klaviyo event; the flow owns the copy</div>
        )}
        <div className='card-meta'>
          <div className='channel-badges'>
            {channels.map(ch => (
              <span key={ch} className={cn('channel-badge', ch)}>
                {ch === 'klaviyo' ? 'klaviyo event' : ch}
              </span>
            ))}
            {areas.map(area => (
              <span key={area} className='meta-badge'>
                {area}
              </span>
            ))}
            {!isUnclassified && <span className='meta-badge'>{humanizeCategory(budget)}</span>}
            {userNotificationSetting && <span className='meta-badge mono'>{userNotificationSetting}</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

TemplateCard.propTypes = {
  template: PropTypes.object.isRequired,
  onSelect: PropTypes.func
};

const CardPushPreview = ({ push }) => {
  const title = push.title || 'Notification';
  const subtitle = push.subtitle || '';
  return (
    <div className='card-push-preview'>
      <div className='mini-push'>
        <div className='mini-push-header'>
          <div className='mini-push-icon'>S</div>
          <span className='mini-push-app'>ShopMy</span>
          <span className='mini-push-time'>now</span>
        </div>
        <div className='mini-push-title'>{title}</div>
        {subtitle && <div className='mini-push-subtitle'>{subtitle}</div>}
      </div>
    </div>
  );
};

CardPushPreview.propTypes = {
  push: PropTypes.object.isRequired
};

// Klaviyo-delivered templates have nothing to render; show the event and the properties the flow sees.
const CardEventPreview = ({ event }) => {
  if (event.error) return <div className='card-event-preview error'>Event failed to render with the mock data</div>;
  const properties = Object.keys(event.eventProperties || {});
  return (
    <div className='card-event-preview'>
      <div className='event-name'>{event.eventName}</div>
      <div className='event-properties'>{properties.join(' · ')}</div>
    </div>
  );
};

CardEventPreview.propTypes = {
  event: PropTypes.object.isRequired
};

NotificationsCatalog.propTypes = {
  templates: PropTypes.array.isRequired,
  isFetching: PropTypes.bool.isRequired,
  onSelectTemplate: PropTypes.func,
  toolbarSlot: PropTypes.instanceOf(Element)
};

export default NotificationsCatalog;
